/**
 * เบี้ยขยันยอดคงที่ (จ่ายในรอบค่าคอม) — ตั้งยอด + ชนิดลาที่ไม่ตัดสิทธิ์ + ยืนยันใบรับรองแพทย์ของใบลาป่วย.
 * คนละตัวกับ "เบี้ยขยันขั้นบันได" ท้ายหน้าเงินเพิ่ม ซึ่งคิดตอนทำเงินเดือน.
 */
import { useState } from 'react';
import { Award, FileText, Save, ScanFace, Stethoscope } from 'lucide-react';
import { DILIGENCE_ALLOWED_LEAVE_VALUES } from '@payroll/shared';
import {
  useDiligenceLadder,
  useDiligenceSettings,
  useForgetScanSettings,
  useMedicalCerts,
  useMyDeniedCapabilities,
  useSaveDiligenceSettings,
  useSaveForgetScanSettings,
  useVerifyMedicalCert,
  type MedicalCertItem,
} from '../../hooks';
import { resolveFileUrl } from '../../lib/api-client';
import { useAuthStore } from '../../lib/store';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState';

type AllowedLeave = (typeof DILIGENCE_ALLOWED_LEAVE_VALUES)[number];

const ALLOWED_LEAVE_LABEL: Record<AllowedLeave, string> = {
  sick_with_cert: 'ลาป่วยมีใบรับรองแพทย์ (ยืนยันโดย HR)',
  sick: 'ลาป่วย',
  personal: 'ลากิจ',
  vacation: 'ลาพักร้อน',
  unpaid: 'ลาไม่รับค่าจ้าง',
  maternity: 'ลาคลอด',
};

const MAX_AMOUNT = 100_000; // ตรงกับเพดานที่ worker รับ
const MAX_FORGET_COUNT = 1000; // ตรงกับเพดานที่ worker รับ
const FORGET_EXAMPLE_TIMES = 5;

function parseAllowedLeave(raw: string): AllowedLeave[] {
  const saved = raw.split(',').map((v) => v.trim());
  return DILIGENCE_ALLOWED_LEAVE_VALUES.filter((v) => saved.includes(v));
}

/** งวดของวันนี้ตามเวลาไทย — ตรงกับ cyclePeriodOf ฝั่ง worker: วันที่ 26 เป็นต้นไปอยู่งวดเดือนถัดไป */
function currentCyclePeriod(): string {
  const today = new Date(Date.now() + 7 * 3_600_000).toISOString().slice(0, 10);
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const d = Number(today.slice(8, 10));
  return new Date(Date.UTC(y, d >= 26 ? m : m - 1, 1)).toISOString().slice(0, 7);
}

function thaiDay(iso: string, withYear = false): string {
  return new Intl.DateTimeFormat('th-TH', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' as const } : {}),
    timeZone: 'Asia/Bangkok',
  }).format(new Date(iso.length === 10 ? `${iso}T00:00:00+07:00` : iso));
}

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

function formatBaht(n: number): string {
  return n.toLocaleString('th-TH', { maximumFractionDigits: 2 });
}

/** ยอดบาท 0 ถึงเพดาน ปัดเป็นสตางค์เหมือน worker · ช่องว่าง/ไม่ใช่ตัวเลข = null (Number('') เป็น 0 จึงต้องกันเอง) */
function parseForgetAmount(raw: string): number | null {
  const n = Number(raw);
  if (raw.trim() === '' || !Number.isFinite(n) || n < 0 || n > MAX_AMOUNT) return null;
  return Math.round(n * 100) / 100;
}

function parseForgetCount(raw: string): number | null {
  const n = Number(raw);
  if (raw.trim() === '' || !Number.isInteger(n) || n < 0 || n > MAX_FORGET_COUNT) return null;
  return n;
}

/** ตัวอย่างยอดหักลืมออกในรอบ ตามสูตรเดียวกับ computeForgetScanDeduction · กรอกไม่ครบ = null */
export function forgetOutExample(lowAmount: string, lowCount: string, highAmount: string, times = FORGET_EXAMPLE_TIMES): string | null {
  const low = parseForgetAmount(lowAmount);
  const count = parseForgetCount(lowCount);
  const high = parseForgetAmount(highAmount);
  if (low === null || count === null || high === null) return null;
  const lowTimes = Math.min(count, times);
  const highTimes = times - lowTimes;
  const parts = [
    ...(lowTimes > 0 ? [`${lowTimes}×${formatBaht(low)}`] : []),
    ...(highTimes > 0 ? [`${highTimes}×${formatBaht(high)}`] : []),
  ];
  const totalSatang = lowTimes * Math.round(low * 100) + highTimes * Math.round(high * 100);
  return `ลืมออก ${times} ครั้งในรอบ = ${parts.join(' + ')} = ${formatBaht(totalSatang / 100)} บาท`;
}

export function DiligenceFlatSection() {
  const role = useAuthStore((s) => s.profile?.role);
  const { data: denied = [] } = useMyDeniedCapabilities(role === 'HR');
  // POST ใช้ requireCapability ซึ่งไม่ปล่อย MANAGER — ซ่อนสิ่งที่หลังบ้านปฏิเสธแน่ ๆ
  const canConfigure = role === 'OWNER' || (role === 'HR' && !denied.includes('config_pay'));
  const canVerifyCerts = role === 'OWNER' || (role === 'HR' && !denied.includes('daily_ops'));

  return (
    <div className="space-y-4 mb-6">
      <DiligenceSettingsCard canConfigure={canConfigure} />
      <ForgetScanSettingsCard canConfigure={canConfigure} />
      {canVerifyCerts && <MedicalCertsCard />}
    </div>
  );
}

function DiligenceSettingsCard({ canConfigure }: { canConfigure: boolean }) {
  const { data: cfg, isLoading, error, refetch } = useDiligenceSettings();
  const { data: ladder } = useDiligenceLadder();
  const save = useSaveDiligenceSettings();
  const toast = useToast();
  const consequence = useConsequence();

  const [amount, setAmount] = useState('');
  const [allowed, setAllowed] = useState<AllowedLeave[]>([]);
  const [loaded, setLoaded] = useState(false);

  // hydrate ครั้งเดียวจากค่าที่บันทึกไว้
  if (cfg && !loaded) {
    setAmount(cfg.DILIGENCE_FLAT_AMOUNT);
    setAllowed(parseAllowedLeave(cfg.DILIGENCE_ALLOWED_LEAVE));
    setLoaded(true);
  }

  function toggleLeave(value: AllowedLeave) {
    setAllowed((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  }

  async function handleSave() {
    const n = Number(amount);
    if (amount.trim() === '' || !Number.isFinite(n) || n < 0 || n > MAX_AMOUNT) {
      toast.error(`กรอกยอดเบี้ยขยันเป็นตัวเลข 0 ถึง ${MAX_AMOUNT.toLocaleString('th-TH')} บาท`);
      return;
    }
    const ordered = DILIGENCE_ALLOWED_LEAVE_VALUES.filter((v) => allowed.includes(v));
    const ok = await consequence({
      title: 'บันทึกการตั้งค่าเบี้ยขยัน',
      lines: [
        { label: 'ยอดเบี้ยขยัน', value: `${n.toLocaleString('th-TH')} บาท`, emphasis: true, tone: 'money' },
        {
          label: 'ลาที่ยังได้เบี้ยขยัน',
          value: ordered.length > 0 ? ordered.map((v) => ALLOWED_LEAVE_LABEL[v]).join(', ') : 'ไม่มี (ลาทุกประเภทตัดเบี้ยขยัน)',
        },
      ],
      effects: [
        'รอบค่าคอมที่ยังเป็นร่าง: ตัวเลขที่ระบบคิดเปลี่ยนตามค่าใหม่ ส่วนยอดที่กรอกไว้แล้วไม่เปลี่ยนเอง หน้ารอบจะเตือนยอดที่ไม่ตรงให้แก้',
        'รอบที่จ่ายแล้วไม่เปลี่ยน',
      ],
      alerts: n === 0
        ? [{ level: 'warn', title: 'ยอดเป็น 0 บาท', detail: 'ทุกคนจะไม่ได้เบี้ยขยันจนกว่าจะตั้งยอดใหม่' }]
        : undefined,
      confirmText: 'ยืนยันบันทึก',
    });
    if (!ok) return;
    try {
      await save.mutateAsync({ DILIGENCE_FLAT_AMOUNT: String(n), DILIGENCE_ALLOWED_LEAVE: ordered.join(',') });
      toast.success('บันทึกการตั้งค่าเบี้ยขยันแล้ว');
    } catch (e) {
      toast.error(errorMessage(e, 'บันทึกไม่สำเร็จ'));
    }
  }

  if (isLoading) return <div className="card p-4 text-sm text-gray-500">กำลังโหลดการตั้งค่าเบี้ยขยัน…</div>;
  if (!cfg) {
    return (
      <div className="card">
        <ErrorState detail={errorMessage(error, 'โหลดการตั้งค่าเบี้ยขยันไม่สำเร็จ')} onRetry={() => void refetch()} />
      </div>
    );
  }

  const editable = cfg.available && canConfigure;

  return (
    <div className="card p-4 border-brand-200">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <h3 className="font-semibold flex items-center gap-2">
          <Award className="w-4 h-4 text-brand-600" /> เบี้ยขยัน
        </h3>
        <span className="badge bg-green-50 text-green-700 text-[10px]">ใช้งานอยู่</span>
      </div>
      <p className="text-xs text-gray-500">
        ยอดเดียวกันทุกคน จ่ายในรอบค่าคอม (จ่ายนอกรอบ) · ได้เมื่อเข้างานก่อนเวลาเริ่มกะทุกวันที่มาทำงาน ไม่ขาดงาน และไม่มีการลา ยกเว้นประเภทที่ติ๊กด้านล่าง
      </p>
      {cfg.available && (
        <p className="text-xs text-gray-700 mt-1">
          {cfg.ATTENDANCE_RULES_FROM
            ? `ใช้ตั้งแต่รอบที่เริ่ม ${thaiDay(cfg.ATTENDANCE_RULES_FROM, true)}`
            : 'ยังไม่ได้กำหนดวันเริ่มใช้กฎนี้ — ระบบยังคิดตามกฎเดิม'}
        </p>
      )}
      {ladder?.active && (
        <p className="text-xs text-amber-800 bg-amber-50 rounded p-2 mt-2">
          เบี้ยขยันขั้นบันไดท้ายหน้านี้ยังเปิดอยู่ และจ่ายผ่านเงินเดือนแยกจากรายการนี้ — ตรวจให้แน่ใจว่าไม่จ่ายซ้ำ
        </p>
      )}
      {!cfg.available && cfg.warning && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded p-2 mt-2">{cfg.warning}</p>
      )}

      <div className="space-y-3 mt-3">
        <div>
          <label className="label" htmlFor="diligence-flat-amount">ยอดเบี้ยขยัน (บาท)</label>
          <input
            id="diligence-flat-amount"
            type="number"
            min="0"
            max={MAX_AMOUNT}
            step="0.01"
            inputMode="decimal"
            className="input"
            value={amount}
            disabled={!editable}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        <fieldset className="bg-gray-50 rounded p-2 space-y-1">
          <legend className="text-sm font-medium text-gray-700 px-1">ลาประเภทที่ติ๊ก ยังได้เบี้ยขยัน</legend>
          {DILIGENCE_ALLOWED_LEAVE_VALUES.map((value) => (
            <label key={value} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={allowed.includes(value)}
                disabled={!editable}
                onChange={() => toggleLeave(value)}
              />
              {ALLOWED_LEAVE_LABEL[value]}
            </label>
          ))}
          {allowed.includes('sick') && (
            <p className="text-xs text-amber-800 pt-1">
              ติ๊ก &ldquo;ลาป่วย&rdquo; = ลาป่วยทุกใบยังได้เบี้ยขยัน แม้ไม่มีใบรับรองแพทย์
            </p>
          )}
          {allowed.length === 0 && (
            <p className="text-xs text-amber-800 pt-1">ไม่ติ๊กเลย = ลาทุกประเภทตัดเบี้ยขยัน</p>
          )}
        </fieldset>

        {editable && (
          <button type="button" onClick={() => void handleSave()} disabled={save.isPending} className="btn-cta w-full">
            <Save className="w-4 h-4" /> {save.isPending ? 'กำลังบันทึก…' : 'บันทึกการตั้งค่าเบี้ยขยัน'}
          </button>
        )}
        {cfg.available && !canConfigure && (
          <p className="text-xs text-gray-500">บัญชีนี้ดูค่าได้อย่างเดียว ไม่มีสิทธิ์แก้การตั้งค่าเบี้ยขยัน</p>
        )}
      </div>
    </div>
  );
}

function ForgetScanSettingsCard({ canConfigure }: { canConfigure: boolean }) {
  const { data: cfg, isLoading, error, refetch } = useForgetScanSettings();
  const save = useSaveForgetScanSettings();
  const toast = useToast();
  const consequence = useConsequence();

  const [inAmount, setInAmount] = useState('');
  const [lowAmount, setLowAmount] = useState('');
  const [lowCount, setLowCount] = useState('');
  const [highAmount, setHighAmount] = useState('');
  const [warningAt, setWarningAt] = useState('');
  const [cumulative, setCumulative] = useState(true);
  const [loaded, setLoaded] = useState(false);

  // hydrate ครั้งเดียวจากค่าที่บันทึกไว้
  if (cfg && !loaded) {
    setInAmount(String(cfg.forget_in_amount));
    setLowAmount(String(cfg.forget_out_low_amount));
    setLowCount(String(cfg.forget_out_low_count));
    setHighAmount(String(cfg.forget_out_high_amount));
    setWarningAt(String(cfg.forget_warning_at));
    setCumulative(cfg.forget_warning_cumulative);
    setLoaded(true);
  }

  async function handleSave() {
    const amountError = `กรอกยอดเป็นตัวเลข 0 ถึง ${MAX_AMOUNT.toLocaleString('th-TH')} บาท`;
    const countError = `เป็นจำนวนเต็ม 0 ถึง ${MAX_FORGET_COUNT.toLocaleString('th-TH')}`;
    const forgetIn = parseForgetAmount(inAmount);
    const low = parseForgetAmount(lowAmount);
    const count = parseForgetCount(lowCount);
    const high = parseForgetAmount(highAmount);
    const at = parseForgetCount(warningAt);
    if (forgetIn === null) { toast.error(`หักลืมเข้า: ${amountError}`); return; }
    if (low === null) { toast.error(`หักลืมออกช่วงแรก: ${amountError}`); return; }
    if (count === null) { toast.error(`จำนวนครั้งช่วงแรก: ${countError}`); return; }
    if (high === null) { toast.error(`หักลืมออกครั้งถัดไป: ${amountError}`); return; }
    if (at === null) { toast.error(`แจ้งเตือนเมื่อถึงครั้งที่: ${countError}`); return; }

    const example = forgetOutExample(lowAmount, lowCount, highAmount);
    const alerts = [
      ...(at === 0 ? [{ level: 'warn' as const, title: 'ปิดการแจ้งเตือน', detail: 'พนักงานและ HR จะไม่เห็นการแจ้งเตือนว่าอาจมีใบเตือน' }] : []),
      ...(forgetIn === 0 && low === 0 && high === 0 ? [{ level: 'warn' as const, title: 'ยอดหักเป็น 0 บาททั้งหมด', detail: 'ลืมสแกนหน้าจะไม่ถูกหักเงินจนกว่าจะตั้งยอดใหม่' }] : []),
    ];
    const ok = await consequence({
      title: 'บันทึกการตั้งค่าลืมสแกนหน้า',
      lines: [
        { label: 'หักลืมเข้า', value: `${formatBaht(forgetIn)} บาท/ครั้ง`, tone: 'money' },
        { label: `หักลืมออก ${count} ครั้งแรก`, value: `${formatBaht(low)} บาท/ครั้ง`, tone: 'money' },
        { label: 'หักลืมออกครั้งถัดไป', value: `${formatBaht(high)} บาท/ครั้ง`, tone: 'money' },
        {
          label: 'แจ้งเตือนเมื่อถึงครั้งที่',
          value: at === 0 ? 'ปิด' : `${at} (${cumulative ? 'นับสะสมข้ามรอบ' : 'นับเฉพาะในรอบ'})`,
        },
      ],
      effects: [
        ...(example ? [`ตัวอย่าง: ${example}`] : []),
        'เงินเดือนงวดที่ยังเป็นร่าง: ต้องรันร่างใหม่ตัวเลขถึงจะเปลี่ยน และคนที่ตรวจรายการหักไว้แล้วอาจต้องตรวจใหม่',
        'รอบค่าคอมที่ยังเป็นร่าง: ตัวเลขที่ระบบคิดเปลี่ยนตามค่าใหม่ ส่วนยอดที่กรอกไว้แล้วไม่เปลี่ยนเอง หน้ารอบจะเตือนยอดที่ไม่ตรงให้แก้',
        'สลิปที่ปิดงวดแล้วและรอบที่จ่ายแล้วไม่เปลี่ยน',
      ],
      alerts: alerts.length > 0 ? alerts : undefined,
      confirmText: 'ยืนยันบันทึก',
    });
    if (!ok) return;
    try {
      await save.mutateAsync({
        forget_in_amount: forgetIn,
        forget_out_low_amount: low,
        forget_out_low_count: count,
        forget_out_high_amount: high,
        forget_warning_at: at,
        forget_warning_cumulative: cumulative,
      });
      // ให้ช่องตรงกับค่าที่บันทึกจริง (worker ปัดยอดเป็นสตางค์)
      setInAmount(String(forgetIn));
      setLowAmount(String(low));
      setLowCount(String(count));
      setHighAmount(String(high));
      setWarningAt(String(at));
      toast.success('บันทึกการตั้งค่าลืมสแกนหน้าแล้ว');
    } catch (e) {
      toast.error(errorMessage(e, 'บันทึกไม่สำเร็จ'));
    }
  }

  if (isLoading) return <div className="card p-4 text-sm text-gray-500">กำลังโหลดการตั้งค่าลืมสแกนหน้า…</div>;
  if (!cfg) {
    return (
      <div className="card">
        <ErrorState detail={errorMessage(error, 'โหลดการตั้งค่าลืมสแกนหน้าไม่สำเร็จ')} onRetry={() => void refetch()} />
      </div>
    );
  }

  const editable = cfg.available && canConfigure;
  const example = forgetOutExample(lowAmount, lowCount, highAmount);
  const amountFields = [
    { id: 'forget-in-amount', label: 'หักลืมเข้า (บาท/ครั้ง)', value: inAmount, set: setInAmount },
    { id: 'forget-out-low-amount', label: 'หักลืมออกช่วงแรก (บาท/ครั้ง)', value: lowAmount, set: setLowAmount },
    { id: 'forget-out-high-amount', label: 'หักลืมออกครั้งถัดไป (บาท/ครั้ง)', value: highAmount, set: setHighAmount },
  ];

  return (
    <div className="card p-4">
      <h3 className="font-semibold flex items-center gap-2 mb-1">
        <ScanFace className="w-4 h-4 text-brand-600" /> ลืมสแกนหน้า
      </h3>
      <p className="text-xs text-gray-500">
        หักเมื่อสแกนเข้าหรือออกอย่างใดอย่างหนึ่งไม่ครบ · ไม่สแกนเลยทั้งวันนับเป็นขาดงาน ไม่ใช่ลืม
      </p>
      {cfg.available && (
        <p className="text-xs text-gray-700 mt-1">
          {cfg.attendance_rules_from
            ? `ใช้ตั้งแต่ ${thaiDay(cfg.attendance_rules_from, true)}`
            : 'ยังไม่ได้กำหนดวันเริ่มใช้กฎนี้ — ระบบยังไม่หักลืมสแกน'}
        </p>
      )}
      {!cfg.available && cfg.warning && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded p-2 mt-2">{cfg.warning}</p>
      )}

      <div className="space-y-3 mt-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {amountFields.map((f) => (
            <div key={f.id}>
              <label className="label" htmlFor={f.id}>{f.label}</label>
              <input
                id={f.id}
                type="number"
                min="0"
                max={MAX_AMOUNT}
                step="0.01"
                inputMode="decimal"
                className="input"
                value={f.value}
                disabled={!editable}
                onChange={(e) => f.set(e.target.value)}
              />
            </div>
          ))}
          <div>
            <label className="label" htmlFor="forget-out-low-count">จำนวนครั้งช่วงแรก</label>
            <input
              id="forget-out-low-count"
              type="number"
              min="0"
              max={MAX_FORGET_COUNT}
              step="1"
              inputMode="numeric"
              className="input"
              value={lowCount}
              disabled={!editable}
              onChange={(e) => setLowCount(e.target.value)}
            />
          </div>
        </div>

        <p className="text-xs text-gray-700 bg-gray-50 rounded p-2">
          {example ? `ตัวอย่าง: ${example}` : 'กรอกยอดและจำนวนครั้งให้ครบเพื่อดูตัวอย่าง'}
          <span className="block text-gray-500 mt-0.5">จำนวนครั้งที่ใช้คิดเงินเริ่มนับใหม่ทุกรอบ 26-25 เสมอ</span>
        </p>

        <fieldset className="bg-gray-50 rounded p-2 space-y-2">
          <legend className="text-sm font-medium text-gray-700 px-1">แจ้งเตือนว่าอาจมีใบเตือน</legend>
          <div>
            <label className="label" htmlFor="forget-warning-at">แจ้งเตือนเมื่อถึงครั้งที่ (0 = ปิด)</label>
            <input
              id="forget-warning-at"
              type="number"
              min="0"
              max={MAX_FORGET_COUNT}
              step="1"
              inputMode="numeric"
              className="input"
              value={warningAt}
              disabled={!editable}
              onChange={(e) => setWarningAt(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={cumulative}
              disabled={!editable}
              onChange={(e) => setCumulative(e.target.checked)}
            />
            นับสะสมข้ามรอบ
          </label>
          <p className="text-xs text-gray-500">
            นับเฉพาะลืมสแกนออก · มีผลกับการแจ้งเตือนเท่านั้น ไม่เกี่ยวกับยอดเงิน · ระบบไม่ออกใบเตือนเอง HR เป็นผู้พิจารณา
          </p>
        </fieldset>

        {editable && (
          <button type="button" onClick={() => void handleSave()} disabled={save.isPending} className="btn-cta w-full">
            <Save className="w-4 h-4" /> {save.isPending ? 'กำลังบันทึก…' : 'บันทึกการตั้งค่าลืมสแกนหน้า'}
          </button>
        )}
        {cfg.available && !canConfigure && (
          <p className="text-xs text-gray-500">บัญชีนี้ดูค่าได้อย่างเดียว ไม่มีสิทธิ์แก้การตั้งค่าลืมสแกนหน้า</p>
        )}
      </div>
    </div>
  );
}

function MedicalCertsCard() {
  const [period, setPeriod] = useState(currentCyclePeriod);
  const { data, isLoading, error, refetch } = useMedicalCerts(period);
  const { data: settings } = useDiligenceSettings();

  const allowed = settings ? parseAllowedLeave(settings.DILIGENCE_ALLOWED_LEAVE) : [];
  const rulesFrom = settings?.ATTENDANCE_RULES_FROM ?? null;

  // การยืนยันไม่มีผลกับเงินในกรณีเหล่านี้ — ต้องบอก ไม่งั้น HR กดแล้วนึกว่าได้เบี้ยขยัน
  let noEffectNote: string | null = null;
  if (settings?.available) {
    if (data && (!rulesFrom || data.window_start < rulesFrom)) {
      noEffectNote = 'งวดนี้คิดตามกฎเดิมซึ่งไม่ดูการลา การยืนยันจึงไม่มีผลกับเบี้ยขยันของงวดนี้';
    } else if (allowed.includes('sick')) {
      noEffectNote = 'ตอนนี้ติ๊ก "ลาป่วย" ไว้ ลาป่วยทุกใบยังได้เบี้ยขยันอยู่แล้ว การยืนยันจึงไม่มีผล';
    } else if (!allowed.includes('sick_with_cert')) {
      noEffectNote = 'ตอนนี้ไม่ได้ติ๊ก "ลาป่วยมีใบรับรองแพทย์" การยืนยันจึงยังไม่มีผลกับเบี้ยขยัน';
    }
  }

  return (
    <div className="card p-4">
      <h3 className="font-semibold flex items-center gap-2 mb-1">
        <Stethoscope className="w-4 h-4 text-brand-600" /> ยืนยันใบรับรองแพทย์
      </h3>
      <p className="text-xs text-gray-500">
        ไฟล์ที่พนักงานแนบยังไม่นับเป็นใบรับรองแพทย์ จนกว่า HR จะเปิดดูแล้วยืนยันว่าออกโดยโรงพยาบาล · ยืนยันครั้งเดียวมีผลทั้งใบลา
      </p>

      <div className="flex items-end gap-3 flex-wrap mt-3">
        <div>
          <label className="label" htmlFor="medical-cert-period">งวด</label>
          <input
            id="medical-cert-period"
            type="month"
            className="input"
            value={period}
            onChange={(e) => { if (e.target.value) setPeriod(e.target.value); }}
          />
        </div>
        {data && (
          <p className="text-xs text-gray-500 pb-2">
            รอบ {thaiDay(data.window_start, true)} ถึง {thaiDay(data.window_end, true)}
          </p>
        )}
      </div>

      {noEffectNote && (
        <p className="text-xs text-amber-800 bg-amber-50 rounded p-2 mt-3">{noEffectNote}</p>
      )}

      {isLoading ? (
        <p className="py-8 text-center text-sm text-gray-500">กำลังโหลด…</p>
      ) : !data ? (
        <ErrorState detail={errorMessage(error, 'โหลดรายการใบลาป่วยไม่สำเร็จ')} onRetry={() => void refetch()} />
      ) : !data.available ? (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded p-2 mt-3">
          {data.warning ?? 'ยังยืนยันใบรับรองแพทย์ไม่ได้'}
        </p>
      ) : data.items.length === 0 ? (
        <EmptyState icon={FileText} title="งวดนี้ไม่มีใบลาป่วยที่อนุมัติแล้ว" />
      ) : (
        <div className="overflow-x-auto max-h-96 overflow-y-auto mt-3">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-left text-[11px] text-gray-500 border-b border-gray-100">
                <th className="py-2 pr-3 font-medium">พนักงาน</th>
                <th className="py-2 pr-3 font-medium">วันที่ลา</th>
                <th className="py-2 pr-3 font-medium">ไฟล์</th>
                <th className="py-2 font-medium">ใบรับรองแพทย์</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => <MedicalCertRow key={item.request_group_id} item={item} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** แยกเป็น component เพื่อให้สถานะกำลังบันทึกเป็นของแถวนั้น ไม่ล็อกทั้งตาราง */
function MedicalCertRow({ item }: { item: MedicalCertItem }) {
  const verify = useVerifyMedicalCert();
  const toast = useToast();
  const myEmpCode = useAuthStore((s) => s.profile?.emp_code ?? null);

  const isOwnLeave = !!myEmpCode && myEmpCode === item.emp_code;
  const fileUrl = resolveFileUrl(item.evidence_url);
  // ใบเดียวกันอาจมีวันที่อนุมัติเพิ่มทีหลัง — ยืนยันไว้แล้วบางวัน แต่ยังไม่ครบทั้งใบ
  const partlyVerified = !item.verified && !!item.verified_at;

  async function toggle(verified: boolean) {
    try {
      await verify.mutateAsync({ leave_id: item.leave_id, verified });
      toast.success(verified ? 'ยืนยันใบรับรองแพทย์แล้ว' : 'ยกเลิกการยืนยันใบรับรองแพทย์แล้ว');
    } catch (e) {
      toast.error(errorMessage(e, 'บันทึกการยืนยันไม่สำเร็จ'));
    }
  }

  return (
    <tr className="border-b border-gray-50 last:border-0 align-top">
      <td className="py-2 pr-3">
        <div className="text-gray-900">
          {item.name ?? item.emp_code}
          {item.nickname && <span className="text-gray-500"> ({item.nickname})</span>}
        </div>
        <div className="text-[11px] text-gray-400">
          {item.department || '—'} · <span className="font-mono">{item.emp_code}</span>
        </div>
        {item.status === 'conditionally_approved' && (
          <div className="text-[11px] text-amber-700">อนุมัติมีเงื่อนไข</div>
        )}
      </td>
      <td className="py-2 pr-3 text-gray-700">{item.dates.map((d) => thaiDay(d)).join(', ')}</td>
      <td className="py-2 pr-3 whitespace-nowrap">
        {fileUrl ? (
          <a href={fileUrl} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">ดูไฟล์</a>
        ) : (
          <span className="text-xs text-gray-400">ไม่มีไฟล์แนบ</span>
        )}
      </td>
      <td className="py-2">
        <label className="flex items-center gap-2 text-sm whitespace-nowrap">
          <input
            type="checkbox"
            checked={item.verified}
            disabled={verify.isPending || isOwnLeave}
            onChange={(e) => void toggle(e.target.checked)}
          />
          ยืนยันใบรับรองแพทย์
        </label>
        {isOwnLeave && <div className="text-[11px] text-gray-400 mt-0.5">ใบลาของตัวเอง ให้ HR คนอื่นยืนยัน</div>}
        {item.verified && item.verified_at && (
          <div className="text-[11px] text-gray-500 mt-0.5">
            {item.verified_by_name ? `โดย ${item.verified_by_name} · ` : ''}{thaiDay(item.verified_at, true)}
          </div>
        )}
        {partlyVerified && (
          <div className="text-[11px] text-amber-700 mt-0.5">ยืนยันไว้บางวัน กดยืนยันอีกครั้งให้ครบทั้งใบ</div>
        )}
      </td>
    </tr>
  );
}
